import { useAuthStore } from '../../store/authStore'
import { useLanguageStore } from '../../store/languageStore'
import { useThemeStore, type Theme } from '../../store/themeStore'
import { useFontStore, type FontFamily, type FontSize } from '../../store/fontStore'
import { formatDate, formatNumber } from '../../utils/formatters'
import { useTranslation } from '../../hooks/useTranslation'
import { useNavigate } from 'react-router-dom'
import { 
  FiSettings, FiMonitor, 
  FiUser, FiKey, FiCheckCircle, FiXCircle, FiLogOut, FiChevronDown,
  FiSun, FiMoon, FiCircle, FiType, FiWifi, FiWifiOff, FiGlobe,
  FiTrendingUp, FiSearch, FiInfo
} from 'react-icons/fi'
import PageRefreshButton from './PageRefreshButton'
import ChangePasswordSlider from '../common/ChangePasswordSlider'
import ProfileSlider from '../common/ProfileSlider'
import UserSettingsSlider from '../common/UserSettingsSlider'
import NotificationCenter from '../common/NotificationCenter'
import { useOfflineSync } from '../../hooks/useOfflineSync'
import { useState, useEffect, useRef } from 'react'
import { getAppVersion } from '../../utils/version'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import FiscalYearSelector from '../common/FiscalYearSelector'
import AnalogClock from '../common/AnalogClock'
import GlobalSearch from '../common/GlobalSearch'
import { useUnifiedCurrency } from '../../hooks/useUnifiedCurrency'
import { useLicenseStatus } from '../../hooks/useLicenseStatus'
import { getRemoteHeartbeatIntervalMs } from '../../services/apiConfig'

interface HeaderProps {
  onLogout: () => void
  onToggleSidebar: () => void
  isSidebarOpen: boolean
}

export default function Header({ onLogout, onToggleSidebar, isSidebarOpen }: HeaderProps) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language, setLanguage } = useLanguageStore()
  const { theme, setTheme } = useThemeStore()
  const { fontFamily, fontSize, setFontFamily, setFontSize } = useFontStore()
  const { t, translate } = useTranslation()
  const { isOnline, pendingCount, syncAll } = useOfflineSync()
  const { currencySymbol } = useUnifiedCurrency()
  const isRTL = language === 'ar' 
  const [currentVersion, setCurrentVersion] = useState<string>(() => getAppVersion())
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showThemeMenu, setShowThemeMenu] = useState(false)
  const [showFontMenu, setShowFontMenu] = useState(false)
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [showUserSettings, setShowUserSettings] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const [dropdownLogoError, setDropdownLogoError] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const themeMenuRef = useRef<HTMLDivElement>(null)
  const fontMenuRef = useRef<HTMLDivElement>(null)
  const languageMenuRef = useRef<HTMLDivElement>(null)

  const { licenseStatus: activeLicense } = useLicenseStatus()

  const licenseDays = activeLicense?.remainingDays ?? 0
  const isLicenseValid = activeLicense?.isValid ?? false

  // Get daily sales summary
  const { data: dailySummary } = useQuery({
    queryKey: ['dailySalesSummary'],
    queryFn: async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.dashboard.summary())
        return {
          totalSales: response.data?.todaySales || 0,
          count: response.data?.invoicesCount || 0
        }
      } catch (error) {
        return { totalSales: 0, count: 0 }
      }
    },
    refetchInterval: getRemoteHeartbeatIntervalMs(5 * 60_000, 5 * 60_000, 5 * 60_000),
    staleTime: 2 * 60_000,
  })

  const dailyTarget = 10000 // Mock target for now
  const dailyProgress = Math.min(100, ((dailySummary?.totalSales || 0) / dailyTarget) * 100)

  // Get current app version
  useEffect(() => {
    setCurrentVersion(getAppVersion())
  }, [])

  // Update document direction and language when language changes
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = language
  }, [language])

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false)
      }
      if (themeMenuRef.current && !themeMenuRef.current.contains(event.target as Node)) {
        setShowThemeMenu(false)
      }
      if (fontMenuRef.current && !fontMenuRef.current.contains(event.target as Node)) {
        setShowFontMenu(false)
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false)
      }
    }

    if (showProfileMenu || showThemeMenu || showFontMenu || showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showProfileMenu, showThemeMenu, showFontMenu, showLanguageMenu])

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme)
    setShowThemeMenu(false)
  }

  const handleLanguageChange = (newLanguage: 'ar' | 'en' | 'fr') => {
    setLanguage(newLanguage)
    setShowLanguageMenu(false)
    // Update document direction and language
    document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = newLanguage
  }

  const getLanguageLabel = (lang: 'ar' | 'en' | 'fr') => {
    const labels = {
      ar: { ar: 'العربية', en: 'Arabic', fr: 'Arabe' },
      en: { ar: 'English', en: 'English', fr: 'Anglais' },
      fr: { ar: 'Français', en: 'French', fr: 'Français' },
    }
    return labels[lang][language] || labels[lang].en
  }

  const getThemeIcon = (themeType: Theme) => {
    switch (themeType) {
      case 'dark':
        return <FiMoon className="text-lg" />
      case 'light':
        return <FiSun className="text-lg" />
      case 'normal':
        return <FiCircle className="text-lg" />
    }
  }

  const getThemeLabel = (themeType: Theme) => {
    if (isRTL) {
      switch (themeType) {
        case 'dark':
          return 'داكن'
        case 'light':
          return 'فاتح'
        case 'normal':
          return 'عادي'
      }
    } else {
      switch (themeType) {
        case 'dark':
          return 'Dark'
        case 'light':
          return 'Light'
        case 'normal':
          return 'Normal'
      }
    }
  }

  const handleFontFamilyChange = (newFontFamily: FontFamily) => {
    setFontFamily(newFontFamily)
    setShowFontMenu(false)
  }

  const handleFontSizeChange = (newFontSize: FontSize) => {
    setFontSize(newFontSize)
    setShowFontMenu(false)
  }

  const getFontFamilyLabel = (family: FontFamily) => {
    return family
  }

  const getFontSizeLabel = (size: FontSize) => {
    if (isRTL) {
      switch (size) {
        case 'small':
          return 'صغير'
        case 'medium':
          return 'متوسط'
        case 'large':
          return 'كبير'
        case 'xlarge':
          return 'كبير جداً'
      }
    } else {
      switch (size) {
        case 'small':
          return 'Small'
        case 'medium':
          return 'Medium'
        case 'large':
          return 'Large'
        case 'xlarge':
          return 'X-Large'
      }
    }
  }

  // Get latest version info
  const { data: latestVersion } = useQuery({
    queryKey: ['latestVersion'],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.appVersions.latest)
      return response.data
    },
    refetchInterval: 300000, // Check every 5 minutes
    retry: 1,
  })

  // Fetch company data to get logo - refreshes when company is updated
  const { data: companyData } = useQuery({
    queryKey: ['company', user?.companyId],
    queryFn: async () => {
      if (!user?.companyId) return null
      try {
        const response = await apiClient.get(API_ENDPOINTS.companies.get(user.companyId))
        return response.data
      } catch (error) {
        console.error('Error fetching company data:', error)
        return null
      }
    },
    enabled: !!user?.companyId,
    staleTime: 60000, // Cache for 1 minute (shorter to allow faster updates)
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: 1,
  })

  // Get company logo URL - improved to handle all cases
  const getCompanyLogoUrl = (): string | null => {
    if (!companyData) return null
    
    // Try all possible logo field names
    const logoValue = companyData.logoLogin || companyData.LogoLogin || 
                     companyData.logoUrl || companyData.LogoUrl ||
                     companyData.logo || companyData.Logo
    
    if (!logoValue || typeof logoValue !== 'string' || logoValue.trim() === '' || logoValue === 'null' || logoValue === 'undefined') {
      return null
    }

    // If already a data URL, return as is
    if (logoValue.startsWith('data:')) {
      return logoValue
    }

    // If it's base64 without data URL prefix, add it
    // Check if it looks like base64 (long string, alphanumeric with +, /, =)
    if (logoValue.length > 50 && /^[A-Za-z0-9+/=]+$/.test(logoValue.replace(/\s/g, ''))) {
      // Try to detect image type from base64
      const cleanBase64 = logoValue.replace(/^data:.*?;base64,/, '').trim()
      // Default to jpeg, but could be png
      return `data:image/jpeg;base64,${cleanBase64}`
    }

    // If it's a URL, return as is
    if (logoValue.startsWith('http://') || logoValue.startsWith('https://')) {
      return logoValue
    }

    return null
  }

  const companyLogoUrl = getCompanyLogoUrl()

  // Default SmartAccount Logo Component - Clean & Professional Design
  const SmartAccountLogo = ({ size = 40, className = '' }: { size?: number; className?: string }) => (
    <div 
      className={`rounded-full bg-gradient-to-br from-orange-500 via-orange-400 to-red-500 flex items-center justify-center shadow-lg ${className}`}
      style={{ width: size, height: size }}
    >
      <svg 
        width={size * 0.7} 
        height={size * 0.7} 
        viewBox="0 0 100 100" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Smart Account Logo - Elegant SA Design */}
        {/* Outer glow circle */}
        <circle cx="50" cy="50" r="45" fill="white" opacity="0.2"/>
        
        {/* Letter S - Stylized */}
        <path 
          d="M30 30 Q30 20 40 20 L50 20 Q60 20 60 30 L60 40 Q60 50 50 50 L40 50 Q30 50 30 60 L30 70 Q30 80 40 80 L50 80" 
          stroke="white" 
          strokeWidth="5" 
          strokeLinecap="round"
          fill="none"
        />
        
        {/* Letter A - Stylized */}
        <path 
          d="M65 80 L75 30 M70 55 L80 55" 
          stroke="white" 
          strokeWidth="5" 
          strokeLinecap="round"
        />
        <path 
          d="M65 80 L75 30" 
          stroke="white" 
          strokeWidth="5" 
          strokeLinecap="round"
        />
        
        {/* Center dot - represents smart/connected */}
        <circle cx="50" cy="50" r="6" fill="white"/>
      </svg>
    </div>
  )

  // Reset logo error states when company logo URL changes
  useEffect(() => {
    setLogoError(false)
    setDropdownLogoError(false)
  }, [companyLogoUrl])

  return (
    <header className="px-6 py-1.5 shadow-lg relative z-[100]" style={{ backgroundColor: 'var(--header-bg)', color: 'var(--header-text)' }}>
      <div className="flex items-center justify-between flex-row-reverse">
        {/* Left/Right Side - Profile and Icons Group */}
        <div className="flex items-center gap-2 flex-row-reverse flex-1">
          {/* Profile Icon with Dropdown Menu */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-1.5 p-1 hover:bg-gray-800 rounded-lg transition-colors"
              style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
            >
              <div className="relative" style={{ display: 'block', visibility: 'visible', opacity: 1 }}>
                {companyLogoUrl && !logoError ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-orange-300 shadow-md" style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
                    <img
                      src={companyLogoUrl}
                      alt="Company Logo"
                      className="w-full h-full object-cover"
                      onError={() => setLogoError(true)}
                    />
                  </div>
                ) : (
                  <SmartAccountLogo size={32} className="border-2 border-orange-300 shadow-md" />
                )}
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-900 shadow-sm" style={{ display: 'block', visibility: 'visible', opacity: 1 }}></div>
              </div>
              <FiChevronDown className={`text-gray-300 hover:text-white transition-all duration-200 ${showProfileMenu ? 'rotate-180' : 'rotate-0'}`} style={{ display: 'block', visibility: 'visible', opacity: 1, minWidth: '14px', minHeight: '14px' }} />
            </button>

            {/* Dropdown Menu */}
            {showProfileMenu && (
              <div 
                className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-[9999] overflow-hidden`}
              >
                {/* User Info */}
                <div className="px-4 py-3 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {companyLogoUrl && !dropdownLogoError ? (
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-orange-300 shadow-md">
                          <img
                            src={companyLogoUrl}
                            alt="Company Logo"
                            className="w-full h-full object-cover"
                            onError={() => setDropdownLogoError(true)}
                          />
                        </div>
                      ) : (
                        <SmartAccountLogo size={48} className="border-2 border-orange-300 shadow-md" />
                      )}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-800 shadow-sm"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-gray-400 text-xs">
                          {isRTL ? 'اسم المستخدم' : 'Username'}
                        </span>
                      </div>
                      <p className="text-white font-semibold text-base truncate">
                        {user?.username || 'User'}
                      </p>
                      {user?.email && (
                        <p className="text-gray-400 text-xs truncate mt-1">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      setShowProfile(true)
                    }}
                    className={`w-full px-4 py-2 text-gray-300 hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <FiUser className="text-lg flex-shrink-0" />
                    <span>{isRTL ? 'الملف الشخصي' : 'Profile'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      setShowUserSettings(true)
                    }}
                    className={`w-full px-4 py-2 text-gray-300 hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <FiSettings className="text-lg flex-shrink-0" />
                    <span>{isRTL ? 'الإعدادات' : 'Settings'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      navigate('/settings/about')
                    }}
                    className={`w-full px-4 py-2 text-gray-300 hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <FiInfo className="text-lg flex-shrink-0" />
                    <span className="flex-1">
                      {isRTL ? 'حول النظام والتحديثات' : language === 'fr' ? 'À propos' : 'About & Updates'}
                    </span>
                    <span className="rounded bg-gray-700 px-1.5 py-0.5 font-mono text-[10px] text-teal-300">
                      v{currentVersion}
                    </span>
                  </button>
                </div>

                {/* Separator */}
                <div className="border-t border-gray-700"></div>

                {/* Change Password Button */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      setShowChangePassword(true)
                    }}
                    className={`w-full px-4 py-2 text-gray-300 hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <FiKey className="text-lg flex-shrink-0" />
                    <span>{isRTL ? 'تغيير كلمة المرور' : 'Change Password'}</span>
                  </button>
                </div>

                {/* Separator */}
                <div className="border-t border-gray-700"></div>

                {/* Logout Button */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false)
                      onLogout()
                    }}
                    className={`w-full px-4 py-2 text-red-400 hover:bg-red-500/10 flex items-center gap-3 transition-colors ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <FiLogOut className="text-lg flex-shrink-0" />
                    <span>{isRTL ? 'تسجيل الخروج' : 'Logout'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Analog Clock next to profile as requested */}
          <div className="mx-1 hidden md:block" title={isRTL ? 'توقيت النظام' : 'System Time'}>
            <AnalogClock size={30} className="border-2 border-white/10" />
          </div>

          {/* Global Search */}
          <div className="flex items-center bg-gray-800/50 rounded-lg p-0.5 ml-1 flex-row-reverse">
            <div className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer text-teal-400" title={isRTL ? 'بحث سريع' : 'Quick Search'}>
              <FiSearch className="text-base" />
            </div>
          </div>

          {/* Daily Sales Ticker - Smart Feature (Hover to Expand) */}
          <div 
            className="hidden lg:flex items-center group bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-1.5 mx-2 transition-all duration-500 ease-in-out hover:gap-3 hover:px-4 overflow-hidden max-w-[48px] hover:max-w-[500px] shadow-inner cursor-pointer"
            title={isRTL ? 'مبيعات اليوم - مرر الماوس للتفاصيل' : 'Daily Sales - Hover for details'}
          >
            {/* Pulsing Icon - Always visible */}
            <div className="flex items-center justify-center min-w-[28px]">
              <div className="relative">
                <FiTrendingUp className="text-lg text-emerald-400" />
                <div className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              </div>
            </div>

            {/* Expanded Content - Revealed on hover */}
            <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-all duration-500 whitespace-nowrap">
              <div className="w-px h-6 bg-white/10" />
              
              <div className="flex flex-col items-center">
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 leading-none mb-1">
                  {isRTL ? 'مبيعات اليوم' : 'Daily Sales'}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-emerald-400 font-mono">
                    {formatNumber(dailySummary?.totalSales || 0)} {currencySymbol}
                  </span>
                </div>
              </div>

              <div className="w-px h-6 bg-white/10" />
              
              <div className="flex flex-col items-center">
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 leading-none mb-1">
                  {isRTL ? 'العمليات' : 'Trans.'}
                </span>
                <span className="text-sm font-black text-blue-400 font-mono">
                  {formatNumber(dailySummary?.count || 0)}
                </span>
              </div>
              
              <div className="w-px h-6 bg-white/10" />
              
              <div className="flex flex-col items-center min-w-[100px]">
                <div className="flex justify-between w-full mb-1 px-1">
                  <span className="text-[9px] font-black uppercase tracking-tight text-white/40">
                    {isRTL ? 'الهدف' : 'Target'}
                  </span>
                  <span className="text-[9px] font-black text-emerald-400">
                    {Math.round(dailyProgress)}%
                  </span>
                </div>
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-1000 ${
                      dailyProgress >= 100 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 
                      dailyProgress >= 70 ? 'bg-blue-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${dailyProgress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Icons Group */}
          <div className="flex items-center gap-0.5 bg-gray-800/50 rounded-lg p-0.5 flex-row-reverse">
            {/* Refresh current tab + data */}
            <PageRefreshButton
              iconSize={16}
              className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-900/20 transition-all duration-200 disabled:opacity-60"
            />

            {/* Offline Status Indicator */}
            <button
              onClick={() => isOnline ? null : syncAll()}
              className={`p-1.5 rounded-lg transition-all duration-300 flex items-center gap-1.5 ${
                isOnline 
                  ? 'text-green-400 hover:bg-green-900/20' 
                  : 'text-orange-400 hover:bg-orange-900/20 animate-pulse'
              }`}
              title={isOnline 
                ? (isRTL ? 'متصل بالإنترنت' : 'Online') 
                : (isRTL ? `غير متصل - ${pendingCount} عمليات معلقة (انقر للمزامنة)` : `Offline - ${pendingCount} pending items (Click to sync)`)
              }
            >
              {isOnline ? <FiWifi className="text-base" /> : <FiWifiOff className="text-base" />}
              {pendingCount > 0 && (
                <span className="bg-orange-500 text-white text-[9px] font-bold px-1 py-0.5 rounded-full min-w-[16px] text-center">
                  {pendingCount}
                </span>
              )}
            </button>

            {/* Notification Center */}
            <NotificationCenter />

            {/* App version — opens changelog */}
            <button
              onClick={() => navigate('/settings/about')}
              className="px-1.5 py-1 hover:bg-gray-700 rounded-lg transition-colors text-teal-300 font-mono text-[10px] font-bold tracking-wide"
              title={isRTL ? 'حول النظام والتحديثات' : language === 'fr' ? 'À propos et mises à jour' : 'About & Updates'}
            >
              v{currentVersion}
            </button>

            {/* Settings Icon */}
            <button 
              onClick={() => navigate('/settings')}
              className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors text-teal-400" 
              title={isRTL ? 'الإعدادات' : 'Settings'}
            >
              <FiSettings className="text-base" style={{ display: 'block', visibility: 'visible', opacity: 1 }} />
            </button>

            {/* Language Switcher */}
            <div className="relative" ref={languageMenuRef}>
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors text-teal-400"
                title={isRTL ? 'تغيير اللغة' : 'Change Language'}
              >
                <FiGlobe className="text-base" style={{ display: 'block', visibility: 'visible', opacity: 1 }} />
              </button>

              {/* Language Dropdown Menu */}
              {showLanguageMenu && (
                <div 
                  className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-48 rounded-lg shadow-xl border z-[100] overflow-hidden`}
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  <div className="py-2">
                    {(['ar', 'en', 'fr'] as const).map((langOption) => (
                      <button
                        key={langOption}
                        onClick={() => handleLanguageChange(langOption)}
                        className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors ${
                          language === langOption ? 'bg-teal-900/50' : ''
                        }`}
                        style={{
                          color: language === langOption ? '#c4b5fd' : 'var(--text-primary)',
                          backgroundColor: language === langOption ? 'rgba(147, 51, 234, 0.1)' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (language !== langOption) {
                            e.currentTarget.style.backgroundColor = 'var(--hover-bg)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (language !== langOption) {
                            e.currentTarget.style.backgroundColor = 'transparent'
                          }
                        }}
                      >
                        <span className="text-lg font-semibold">
                          {langOption === 'ar' ? 'ع' : langOption === 'en' ? 'EN' : 'FR'}
                        </span>
                        <span className="flex-1">{getLanguageLabel(langOption)}</span>
                        {language === langOption && (
                          <FiCheckCircle className="ml-auto" style={{ color: '#a78bfa' }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Theme Switcher */}
            <div className="relative" ref={themeMenuRef}>
              <button
                onClick={() => setShowThemeMenu(!showThemeMenu)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-teal-400"
                title={isRTL ? 'تغيير الثيم' : 'Change Theme'}
              >
                <span style={{ display: 'block', visibility: 'visible', opacity: 1 }}>
                  {getThemeIcon(theme)}
                </span>
              </button>

              {/* Theme Dropdown Menu */}
              {showThemeMenu && (
                <div 
                  className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-48 rounded-lg shadow-xl border z-[100] overflow-hidden`}
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  <div className="py-2">
                    {(['dark', 'light', 'normal'] as Theme[]).map((themeOption) => (
                      <button
                        key={themeOption}
                        onClick={() => handleThemeChange(themeOption)}
                        className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors ${
                          theme === themeOption ? 'bg-teal-900/50' : ''
                        }`}
                        style={{
                          color: theme === themeOption ? '#c4b5fd' : 'var(--text-primary)',
                          backgroundColor: theme === themeOption ? 'rgba(147, 51, 234, 0.1)' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (theme !== themeOption) {
                            e.currentTarget.style.backgroundColor = 'var(--hover-bg)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (theme !== themeOption) {
                            e.currentTarget.style.backgroundColor = 'transparent'
                          }
                        }}
                      >
                        {getThemeIcon(themeOption)}
                        <span>{getThemeLabel(themeOption)}</span>
                        {theme === themeOption && (
                          <FiCheckCircle className="ml-auto" style={{ color: '#a78bfa' }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Font Settings Switcher */}
            <div className="relative" ref={fontMenuRef}>
              <button
                onClick={() => setShowFontMenu(!showFontMenu)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-teal-400"
                title={isRTL ? 'الخط وحجم الخط' : 'Font & Size'}
              >
                <FiType className="text-lg" style={{ display: 'block', visibility: 'visible', opacity: 1 }} />
              </button>

              {/* Font Settings Dropdown Menu */}
              {showFontMenu && (
                <div 
                  className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-64 rounded-lg shadow-xl border z-[100] overflow-hidden`}
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  {/* Font Family Section */}
                  <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                      {isRTL ? 'نوع الخط' : 'Font Family'}
                    </div>
                    <div className="space-y-1 max-h-96 overflow-y-auto custom-scrollbar">
                      {([
                        'Cairo', 
                        'Tajawal', 
                        'Changa', 
                        'El Messiri', 
                        'Reem Kufi', 
                        'Almarai', 
                        'Noto Sans Arabic', 
                        'Noto Naskh Arabic', 
                        'IBM Plex Sans Arabic', 
                        'Rubik', 
                        'Scheherazade New', 
                        'Harmattan', 
                        'Markazi Text',
                        'Amiri', 
                        'Lateef',
                        'Arial', 
                        'Tahoma', 
                        'Segoe UI', 
                        'Roboto'
                      ] as FontFamily[]).map((fontOption) => {
                        const fontFamilyMap: Record<FontFamily, string> = {
                          'Cairo': "'Cairo', Arial, sans-serif",
                          'Arial': "Arial, sans-serif",
                          'Tahoma': "Tahoma, Arial, sans-serif",
                          'Segoe UI': "'Segoe UI', Tahoma, Arial, sans-serif",
                          'Roboto': "'Roboto', Arial, sans-serif",
                          'Amiri': "'Amiri', 'Cairo', Arial, serif",
                          'Lateef': "'Lateef', 'Cairo', Arial, sans-serif",
                          'Tajawal': "'Tajawal', 'Cairo', Arial, sans-serif",
                          'Changa': "'Changa', 'Cairo', Arial, sans-serif",
                          'El Messiri': "'El Messiri', 'Cairo', Arial, sans-serif",
                          'Reem Kufi': "'Reem Kufi', 'Cairo', Arial, sans-serif",
                          'Almarai': "'Almarai', 'Cairo', Arial, sans-serif",
                          'Noto Sans Arabic': "'Noto Sans Arabic', 'Cairo', Arial, sans-serif",
                          'Noto Naskh Arabic': "'Noto Naskh Arabic', 'Cairo', Arial, sans-serif",
                          'IBM Plex Sans Arabic': "'IBM Plex Sans Arabic', 'Cairo', Arial, sans-serif",
                          'Rubik': "'Rubik', 'Cairo', Arial, sans-serif",
                          'Scheherazade New': "'Scheherazade New', 'Cairo', Arial, serif",
                          'Harmattan': "'Harmattan', 'Cairo', Arial, sans-serif",
                          'Markazi Text': "'Markazi Text', 'Cairo', Arial, serif",
                        }
                        return (
                        <button
                          key={fontOption}
                          onClick={() => handleFontFamilyChange(fontOption)}
                          className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors rounded ${
                            fontFamily === fontOption ? 'bg-teal-900/50' : ''
                          }`}
                          style={{
                            color: fontFamily === fontOption ? '#c4b5fd' : 'var(--text-primary)',
                            backgroundColor: fontFamily === fontOption ? 'rgba(147, 51, 234, 0.1)' : 'transparent',
                            fontFamily: fontFamilyMap[fontOption] || `${fontOption}, Arial, sans-serif`
                          }}
                          onMouseEnter={(e) => {
                            if (fontFamily !== fontOption) {
                              e.currentTarget.style.backgroundColor = 'var(--hover-bg)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (fontFamily !== fontOption) {
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }
                          }}
                        >
                          <span className="flex-1">{getFontFamilyLabel(fontOption)}</span>
                          {fontFamily === fontOption && (
                            <FiCheckCircle className="text-sm" style={{ color: '#a78bfa' }} />
                          )}
                        </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Font Size Section */}
                  <div className="px-4 py-2">
                    <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                      {isRTL ? 'حجم الخط' : 'Font Size'}
                    </div>
                    <div className="space-y-1">
                      {(['small', 'medium', 'large', 'xlarge'] as FontSize[]).map((sizeOption) => (
                        <button
                          key={sizeOption}
                          onClick={() => handleFontSizeChange(sizeOption)}
                          className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors rounded ${
                            fontSize === sizeOption ? 'bg-teal-900/50' : ''
                          }`}
                          style={{
                            color: fontSize === sizeOption ? '#c4b5fd' : 'var(--text-primary)',
                            backgroundColor: fontSize === sizeOption ? 'rgba(147, 51, 234, 0.1)' : 'transparent',
                            fontSize: sizeOption === 'small' ? '12px' :
                                     sizeOption === 'medium' ? '14px' :
                                     sizeOption === 'large' ? '16px' : '18px'
                          }}
                          onMouseEnter={(e) => {
                            if (fontSize !== sizeOption) {
                              e.currentTarget.style.backgroundColor = 'var(--hover-bg)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (fontSize !== sizeOption) {
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }
                          }}
                        >
                          <span className="flex-1">{getFontSizeLabel(sizeOption)}</span>
                          {fontSize === sizeOption && (
                            <FiCheckCircle className="text-sm" style={{ color: '#a78bfa' }} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right/Left Side - License, Fiscal Year, and Tools */}
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : 'flex-row-reverse'}`}>
            <div className="flex flex-col items-stretch gap-0.5">
              {/* License Info */}
              {isLicenseValid ? (
                <button
                  onClick={() => navigate('/settings/license')}
                  className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md cursor-pointer transition-opacity hover:opacity-80 justify-center ${
                    licenseDays <= 30 
                      ? 'bg-red-900/30 border border-red-500/50' 
                      : licenseDays <= 60 
                        ? 'bg-yellow-900/30 border border-yellow-500/50' 
                        : 'bg-green-900/30 border border-green-500/50'
                  }`}
                >
                  {licenseDays <= 30 ? (
                    <FiXCircle className={`text-[10px] ${licenseDays <= 30 ? 'text-red-400' : 'text-yellow-400'}`} />
                  ) : (
                    <FiCheckCircle className="text-[10px] text-green-400" />
                  )}
                  <span className={`text-[11px] font-bold ${
                    licenseDays <= 30 
                      ? 'text-red-300' 
                      : licenseDays <= 60 
                        ? 'text-yellow-300' 
                        : 'text-green-300'
                  }`}>
                    {isRTL 
                      ? `متبقي ${licenseDays} يوم` 
                      : `${licenseDays} days remaining`}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/settings/license')}
                  className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-900/30 border border-red-500/50 cursor-pointer transition-opacity hover:opacity-80 justify-center"
                >
                  <FiXCircle className="text-[10px] text-red-400" />
                  <span className="text-[11px] font-bold text-red-300">
                    {isRTL ? 'الترخيص منتهي' : 'License Expired'}
                  </span>
                </button>
              )}

            {/* Fiscal Year Selector - Moved here to be under license */}
            <div className="relative">
              <FiscalYearSelector />
            </div>
          </div>

          <div className="h-6 w-px bg-gray-700 mx-1"></div>
        </div>
      </div>

      {/* Change Password Slider */}
      <ChangePasswordSlider
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
      <ProfileSlider
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
      />
      <UserSettingsSlider
        isOpen={showUserSettings}
        onClose={() => setShowUserSettings(false)}
      />
    </header>
  )
}

